import React, { useEffect } from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { hot } from "react-hot-loader/root";
import { AbacProvider } from "react-abac";
import styled, { ThemeProvider } from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import "react-tooltip/dist/react-tooltip.css";
import { Transition, TransitionGroup } from "react-transition-group";
import ReactGA from "react-ga4";
import { useDispatch, useSelector, useStore } from "react-redux";
import { EnvironmentEnum, MemberStatusEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import Layout from "~/components/Layout";
import Auth from "~/views/public/Auth";
import Onboarding from "~/views/private/Onboarding";
import rules from "~/lib/rules";
import authActionCreators from "~/store/auth/actions";
import authSelectors from "~/store/auth/selectors";
import NotFound from "~/views/private/NotFound";
import Profile from "~/views/private/Profile";
import SidebarProvider from "~/components/SidebarProvider";
import Members from "~/views/private/Members";
import Member from "~/views/private/Member";
import { enterView, exitView } from "~/lib/animations";
import Spinner from "~/components-shared/Spinner";
import OnboardingComplete from "~/components/OnboardingComplete";
import AccountSettings from "~/views/private/AccountSettings";
import { Tooltip } from "react-tooltip";
import VerifyEmail from "~/views/public/VerifyEmail";
import moment from "moment";
import Activities from "~/views/private/Activities";
import ErrorView from "~/views/private/ErrorView";
import MemberManagement from "~/views/private/MemberManagement";
import initWebsocket from "~/lib/websockets";
import Invitations from "~/views/private/Invitations";
import AppStatusModal from "~/components/AppStatusModal";
import Toasts from "~/components/Toasts";
import initializeSentry from "~/lib/sentry";
import MyWorkingStyle from "~/views/private/MyWorkingStyle";
import PublicWorkingStyle from "~/views/public/PublicWorkingStyle";
import PublicWorkingStyles from "~/views/public/PublicWorkingStyles";
import WorkingStyles from "~/views/private/WorkingStyles";
import WorkingStyle from "~/views/private/WorkingStyle";
import CookieConsent from "~/components/CookieConsent";
import OrgChart from "~/views/private/OrgChart";
import Topics from "~/views/private/Topics";
import Topic from "~/views/private/Topic";
import RoleManagement from "~/views/private/RoleManagement";
import AiPrompts from "~/views/private/AiPrompts";
import CompanyManagement from "~/views/private/CompanyManagement";
import GlobalStyle from "~/components/GlobalStyle";
import useUserTheme from "~/hooks/theme";
import Insights from "~/views/private/Insights";
import Footprints from "~/views/private/Footprints";
import Costs from "~/views/private/Costs";
import TopicOverview from "~/views/private/TopicOverview";
import ProductTrends from "~/views/private/ProductTrends";
import CustomerTrends from "~/views/private/CustomerTrends";
import { helpers } from "~/lib/helpers";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import MemberInsightsExplorer from "~/views/private/MemberInsightsExplorer";
import MemberInsightsReports from "~/views/private/MemberInsightsReports";
import MemberInsightsReport from "~/views/private/MemberInsightsReport";
import CompanyIntegrations from "~/views/public/PublicIntegrations";
import InsightsExplorer from "~/views/private/InsightsExplorer";
import InsightsReports from "~/views/private/InsightsReports";
import InsightsReport from "~/views/private/InsightsReport";
import SignUp from "~/views/public/SignUp";
import SignUpOnboarding from "~/views/private/SignUpOnboarding";
import SignUpOnboardingComplete from "~/components/SignUpOnboardingComplete";
import qs from "qs";
import ProgressUpdates from "~/views/private/ProgressUpdates";
import Integrations from "~/views/private/Integrations";
import PaidPlanCalloutModal from "~/components/PaidPlanCalloutModal";
import { usePostHog } from "posthog-js/react";
const Wrapper = styled.div`
  height: 100%;
`;
const Center = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
`;
const StyledToasts = styled(Toasts)`
  position: fixed;
  z-index: 1000;
  bottom: 20px;
  left: 20px;
`;
moment.locale("en", {
  week: {
    dow: 1
  },
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "seconds",
    ss: "%s seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years"
  }
});
const App = () => {
  const user = useSelector(authSelectors.getUser);
  const posthog = usePostHog();
  const sso = useAuth0();
  const sessionStatus = useSelector(authSelectors.getSessionStatus);
  const integrationStatus = useSelector(integrationSelectors.getStatus);
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const userTheme = useUserTheme(user?.theme);
  const environment = helpers.getEnvironment();
  const {
    authToken
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });
  useEffect(() => {
    if ([EnvironmentEnum.STAGING, EnvironmentEnum.PRODUCTION].includes(environment)) {
      const googleId = {
        [EnvironmentEnum.STAGING]: "G-3V72QNFJQ2",
        [EnvironmentEnum.PRODUCTION]: "G-RC4Q02WV6B"
      };
      ReactGA.initialize(googleId[environment]);
      initializeSentry(history, user);
    }
  }, []);
  useEffect(() => {
    if (sessionStatus === StatusStateEnum.IDLE) {
      if (authToken) {
        dispatch(authActionCreators.updateTokenStarted(authToken));
      }
      dispatch(authActionCreators.continueSessionStarted());
    }
  }, [dispatch, sessionStatus, authToken]);
  useEffect(() => {
    if (user) {
      initWebsocket(store);
      posthog.identify(user.id, {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        company: user.company?.name
      });
    }
  }, [store, user]);
  useEffect(() => {
    if (user && integrationStatus === StatusStateEnum.IDLE) {
      dispatch(integrationsActionCreators.fetchStarted());
      dispatch(integrationsActionCreators.fetchMemberStarted());
    }
  }, [integrationStatus, user]);
  useEffect(() => {
    if (sso.isAuthenticated) {
      dispatch(authActionCreators.exchangeAuth0TokenStarted(sso));
    }
  }, [sso.isAuthenticated]);
  if (sessionStatus === StatusStateEnum.LOADING || sessionStatus === StatusStateEnum.IDLE) {
    return /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(ThemeProvider, {
      theme: userTheme
    }, /*#__PURE__*/React.createElement(Spinner, null)));
  }
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: userTheme
  }, /*#__PURE__*/React.createElement(GlobalStyle, null), /*#__PURE__*/React.createElement(ErrorView, null, user ? /*#__PURE__*/React.createElement(AbacProvider, {
    user: user,
    roles: [user.role.id],
    rules: rules,
    permissions: user.authorities
  }, user.status.includes("SIGN_UP") ? /*#__PURE__*/React.createElement(Switch, {
    location: location
  }, /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.ONBOARDING,
    component: SignUpOnboarding
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "*",
    to: RoutesEnum.ONBOARDING
  })) : /*#__PURE__*/React.createElement(SidebarProvider, null, /*#__PURE__*/React.createElement(Layout, null, /*#__PURE__*/React.createElement(Switch, {
    location: location
  }, /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.GET_STARTED,
    component: Onboarding
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.PROFILE,
    component: Profile
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INVITATIONS,
    component: Invitations
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.ACCOUNT_SETTINGS,
    component: AccountSettings
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INTEGRATIONS,
    component: Integrations
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBERS,
    component: Members
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER,
    component: Member
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.ACTIVITIES,
    component: Activities
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.COMPANY_MANAGEMENT,
    component: CompanyManagement
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER_MANAGEMENT,
    component: MemberManagement
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.ROLE_MANAGEMENT,
    component: RoleManagement
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.WORKING_STYLES,
    component: WorkingStyles
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.WORKING_STYLE,
    component: WorkingStyle
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.WORKING_STYLE_SHORTEN,
    component: WorkingStyle
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MY_WORKING_STYLE,
    component: MyWorkingStyle
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.TOPIC_OVERVIEW,
    component: TopicOverview
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.TOPICS,
    component: Topics
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.TOPIC,
    component: Topic
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.PRODUCT_TRENDS,
    component: ProductTrends
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.CUSTOMER_TRENDS,
    component: CustomerTrends
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INSIGHTS_REPORTS,
    component: Insights
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INSIGHTS_EXPLORER,
    component: InsightsExplorer
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INSIGHTS_CONFIGS,
    component: InsightsReports
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INSIGHTS_CONFIG,
    component: InsightsReport
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INSIGHTS_REPORT,
    component: InsightsReport
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INSIGHTS_REPORT_OLD,
    component: Insights
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.PROGRESS_UPDATES,
    component: ProgressUpdates
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER_INSIGHTS_EXPLORER,
    component: MemberInsightsExplorer
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER_INSIGHTS_CONFIGS,
    component: MemberInsightsReports
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER_INSIGHTS_CONFIG,
    component: MemberInsightsReport
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER_INSIGHTS_REPORT,
    component: MemberInsightsReport
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.ORG_CHART,
    component: OrgChart
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.AI_PROMPTS,
    component: AiPrompts
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.FOOTPRINTS,
    component: Footprints
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.COST_EXPLORER,
    component: Costs
  }), /*#__PURE__*/React.createElement(Redirect, {
    exact: true,
    from: RoutesEnum.AUTH_CALLBACK,
    to: RoutesEnum.TOPIC_OVERVIEW
  }), /*#__PURE__*/React.createElement(Redirect, {
    exact: true,
    from: RoutesEnum.COMPANY_INTEGRATIONS,
    to: RoutesEnum.INTEGRATIONS
  }), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    component: NotFound
  })))), /*#__PURE__*/React.createElement(AppStatusModal, null), /*#__PURE__*/React.createElement(PaidPlanCalloutModal, null), /*#__PURE__*/React.createElement(OnboardingComplete, {
    open: [MemberStatusEnum.ONBOARDING_COMPLETE].includes(user.status)
  }), /*#__PURE__*/React.createElement(SignUpOnboardingComplete, {
    open: [MemberStatusEnum.SIGN_UP_COMPLETE].includes(user.status)
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TransitionGroup, {
    component: null
  }, /*#__PURE__*/React.createElement(Transition, {
    key: location.key,
    onEnter: (node, appears) => enterView(node, appears),
    onExit: node => exitView(node),
    timeout: {
      enter: 750,
      exit: 400
    },
    appear: true
  }, /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.VERIFY_EMAIL,
    component: VerifyEmail
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.LOG_IN,
    component: Auth
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.SIGN_UP,
    component: SignUp
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.WORKING_STYLES,
    component: PublicWorkingStyles
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.WORKING_STYLE,
    component: PublicWorkingStyle
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.WORKING_STYLE_SHORTEN,
    component: PublicWorkingStyle
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.COMPANY_INTEGRATIONS,
    component: CompanyIntegrations
  }), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    component: Auth
  })))), /*#__PURE__*/React.createElement(CookieConsent, null)), /*#__PURE__*/React.createElement(Tooltip, {
    id: "general-tooltip",
    place: "bottom",
    delayShow: 500
  }), /*#__PURE__*/React.createElement(StyledToasts, null))));
};
export default App;